import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, Typography, Card, CardContent, CardActions, Button, MenuItem, TextField, Alert, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, CircularProgress, InputAdornment, TablePagination,
  useTheme, useMediaQuery
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { usersAPI, buildingsAPI, facilitiesAPI, facilityBookingsAPI, flatsAPI } from '../services/api';
import { ArrowBack, ArrowForward, Close, Refresh, Edit, Delete, Search, EventAvailable, Apartment, CalendarMonth } from '@mui/icons-material';

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Helper function to generate slots from timings (fallback for frontend)
function generateSlotsFromTimings(timings, numberOfSlots = 1) {
  if (!timings || typeof timings !== 'string') {
    return [];
  }
  
  // Parse timings like "06:00-18:00" or "6:00-18:00"
  const timingPattern = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/;
  const match = timings.match(timingPattern);
  
  if (!match) {
    return [];
  }
  
  const startHour = parseInt(match[1]);
  const startMin = parseInt(match[2]);
  const endHour = parseInt(match[3]);
  const endMin = parseInt(match[4]);
  
  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;
  const totalMinutes = endMinutes - startMinutes;
  
  if (totalMinutes <= 0 || numberOfSlots <= 0) {
    return [];
  }
  
  const slotDuration = Math.floor(totalMinutes / numberOfSlots);
  const slots = [];
  
  for (let i = 0; i < numberOfSlots; i++) {
    const slotStart = startMinutes + (i * slotDuration);
    const slotEnd = i === numberOfSlots - 1 ? endMinutes : slotStart + slotDuration;
    
    const startH = Math.floor(slotStart / 60).toString().padStart(2, '0');
    const startM = (slotStart % 60).toString().padStart(2, '0');
    const endH = Math.floor(slotEnd / 60).toString().padStart(2, '0');
    const endM = (slotEnd % 60).toString().padStart(2, '0');
    
    slots.push(`${startH}:${startM}-${endH}:${endM}`);
  }
  
  return slots;
}

function getSlotStatus(date, slot, bookedSlots, availableSlots, today, now) {
  // date: yyyy-mm-dd, slot: "06:00-10:00"
  const slotStart = slot.split('-')[0];
  const slotEnd = slot.split('-')[1];
  const slotBookedCount = bookedSlots.filter(s => s === slot).length;
  
  // No debug logging in production
  
if (new Date(date).setHours(0,0,0,0) < today.setHours(0,0,0,0)) return 'past';  if (new Date(date).toDateString() === today.toDateString()) {
    // If slot end time is before now, it's past
    const slotEndDate = new Date(date + 'T' + slotEnd + ':00');
    if (slotEndDate < now) return 'past';
  }
  if (slotBookedCount >= availableSlots) return 'booked';
  return 'available';
}

const FacilityBooking = () => {
  const { t } = useTranslation();
  const { user, loading: userLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Changed back to 'md' for better responsiveness

  // All hooks at the top level!
  const [selectedFacility, setSelectedFacility] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [selectedFlat, setSelectedFlat] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [monthSlots, setMonthSlots] = useState({});
  const [editingBooking, setEditingBooking] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const queryClient = useQueryClient();

  const userId =  user.attributes?.sub;
  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  let isBuildingAdmin = false;
  if (groupNames.includes('building_admin') || ['super_admin', 'building_admin'].includes(user?.role)) {
    isBuildingAdmin = true;
  }
  
  // Alternative check: if user has building associations without flatId, they might be a building admin
  // We'll determine this after fetching associations
  
  
  // Fetch user's building associations
  const { data: userAssociations = [], isLoading: loadingAssociations } = useQuery(
    ['userAssociations', userId],
    async () => {
      console.log('=== USER ASSOCIATIONS QUERY ===');
      console.log('userId:', userId);
      
      if (!userId) {
        console.log('No userId, returning empty array');
        return [];
      }
      
      try {
        console.log('Fetching user associations for userId:', userId);
        const response = await usersAPI.getAssociations(userId);
        console.log('User associations response:', response);
        console.log('Response data:', response.data);
        
        const data = response.data;
        // Ensure we always return an array
        if (Array.isArray(data)) {
          console.log('Returning data as array:', data);
          return data;
        }
        if (Array.isArray(data?.associations)) {
          console.log('Returning data.associations:', data.associations);
          return data.associations;
        }
        if (Array.isArray(data?.userAssociations)) {
          console.log('Returning data.userAssociations:', data.userAssociations);
          return data.userAssociations;
        }
        console.log('No valid array found, returning empty array');
        return [];
      } catch (error) {
        console.error('Error fetching user associations:', error);
        console.error('Error details:', error.response);
        return [];
      }
    },
    { enabled: !!userId }
  );
  
  // Get buildingIds from associations - ensure userAssociations is an array
  const buildingIds = useMemo(() => {
    const ids = Array.isArray(userAssociations) ? userAssociations.map(a => a.buildingId) : [];
    console.log('=== BUILDING IDS CALCULATION ===');
    console.log('userAssociations:', userAssociations);
    console.log('userAssociations isArray:', Array.isArray(userAssociations));
    console.log('calculated buildingIds:', ids);
    console.log('===================================');
    return ids;
  }, [userAssociations]);
  
  // Enhanced building admin detection: also check if user has building associations without flatId
  const hasBuildingOnlyAssociations = Array.isArray(userAssociations) && 
    userAssociations.some(a => a.buildingId && !a.flatId);
  const finalIsBuildingAdmin = isBuildingAdmin || hasBuildingOnlyAssociations;
  
  // building admin detection computed above
  
  // Debug: For building admins, if no buildingIds from associations, try to get from buildings API
  useEffect(() => {
    if (finalIsBuildingAdmin && buildingIds.length === 0 && userAssociations.length === 0) {
      console.warn('Building admin detected but no building associations found');
    }
  }, [finalIsBuildingAdmin, buildingIds, userAssociations]);

  // For adminFlats
  const { data: adminFlats = [], isLoading: loadingAdminFlats } = useQuery(
    ['adminFlats', buildingIds],
    async () => {
      if (!finalIsBuildingAdmin || !Array.isArray(buildingIds) || buildingIds.length === 0) return [];
      try {
        const allFlats = await Promise.all(
          buildingIds.map(id => flatsAPI.getByBuilding(id).then(res => res.data))
        );
        return Array.isArray(allFlats) ? allFlats.flat() : [];
      } catch (error) {
        console.error('Error fetching admin flats:', error);
        return [];
      }
    },
    { enabled: finalIsBuildingAdmin && Array.isArray(buildingIds) && buildingIds.length > 0 }
  );
  
  // For userFlats - Regular users should see their own flats
  const userFlats = Array.isArray(userAssociations)
  ? userAssociations
      .filter(a => {
        return a.flatId; // Only associations with flatId
      })
      .map(a => ({
        flatId: a.flatId,
        flatNumber: a.flatNumber,
        buildingName: a.buildingName,
        buildingId: a.buildingId, // Also include buildingId for consistency
      }))
  : [];
  
  // admin/user flats computed above
  
  // For building admins, if no adminFlats, also try to show associations without filtering by flatId
  let additionalFlats = [];
  if (finalIsBuildingAdmin && Array.isArray(userAssociations) && userAssociations.length > 0) {
    additionalFlats = userAssociations
      .filter(a => !a.flatId && a.buildingId) // Building-only associations for admins
      .map(a => ({
        flatId: a.buildingId, // Use buildingId as fallback ID
        flatNumber: `All Flats`, // Generic label for building admin
        buildingName: a.buildingName,
        buildingId: a.buildingId,
      }));
  }
  
  // Use adminFlats if user is building admin, otherwise userFlats
  const flatsForDropdown = finalIsBuildingAdmin ? [...adminFlats, ...additionalFlats] : userFlats;
  const loadingFlats = finalIsBuildingAdmin ? loadingAdminFlats : loadingAssociations;
  
  // flatsForDropdown and loading flags are used above

  // Add effect to log when data changes
  useEffect(() => {
    // Data update effect - no debug logging in production
  }, [userAssociations, buildingIds, flatsForDropdown, finalIsBuildingAdmin]);
  
  // Fetch buildings for user's associations
  const { data: buildings = [] } = useQuery(
    ['buildings', buildingIds],
    async () => {
      if (!Array.isArray(buildingIds) || buildingIds.length === 0) return [];
      try {
        const buildingResponses = await Promise.all(buildingIds.map(id => buildingsAPI.getById(id)));
        return buildingResponses.map(res => res.data);
      } catch (error) {
        console.error('Error fetching buildings:', error);
        return [];
      }
    },
    { enabled: Array.isArray(buildingIds) && buildingIds.length > 0 }
  );

  // Fetch facilities for all user's buildings with auto-refresh
  const { data: facilities = [], isLoading: loadingFacilities, error: facilitiesError, refetch: refetchFacilities } = useQuery(
    ['facilities', buildingIds],
    async () => {
      console.log('=== FACILITIES QUERY EXECUTING ===');
      console.log('buildingIds:', buildingIds);
      console.log('buildingIds type:', typeof buildingIds);
      console.log('buildingIds isArray:', Array.isArray(buildingIds));
      console.log('buildingIds length:', buildingIds?.length);
      
      if (!Array.isArray(buildingIds) || buildingIds.length === 0) {
        console.warn('No building IDs available for facilities fetch');
        return [];
      }
      
      console.log('Starting facility fetch for buildings:', buildingIds);
      
      try {
        // Test with a single building first
        const results = [];
        for (const id of buildingIds) {
          try {
            console.log(`Fetching facilities for building: ${id}`);
            const res = await facilitiesAPI.getAll(id);
            console.log(`Raw response for building ${id}:`, res);
            console.log(`Response data for building ${id}:`, res?.data);
            
            if (res && res.data && Array.isArray(res.data)) {
              console.log(`Adding ${res.data.length} facilities from building ${id}`);
              
              // Log slot information for each facility
              res.data.forEach((facility, index) => {
                console.log(`Facility ${index + 1} (${facility.name}):`, {
                  facilityId: facility.facilityId,
                  timings: facility.timings,
                  numberOfSlots: facility.numberOfSlots,
                  slots: facility.slots,
                  slotsCount: facility.slots?.length || 0
                });
              });
              
              results.push(...res.data);
            } else {
              console.warn(`Invalid response format for building ${id}:`, res);
            }
          } catch (error) {
            console.error(`Error fetching facilities for building ${id}:`, error);
            console.error(`Error details:`, error.response);
          }
        }
        
        console.log('Final facilities result:', results);
        console.log('Total facilities found:', results.length);
        return results;
      } catch (error) {
        console.error('Error in facilities query:', error);
        return [];
      }
    },
    { 
      enabled: (() => {
        const isEnabled = Array.isArray(buildingIds) && buildingIds.length > 0;
        console.log('Facilities query enabled check:', {
          isEnabled,
          buildingIds,
          buildingIdsIsArray: Array.isArray(buildingIds),
          buildingIdsLength: buildingIds?.length
        });
        return isEnabled;
      })(),
      refetchInterval: false, // Disable automatic refetch 
      refetchIntervalInBackground: false, // Disable background refresh to reduce calls
      staleTime: 300000, // Data is considered fresh for 5 minutes
      cacheTime: 600000, // Keep in cache for 10 minutes
    }
  );
  
  // Debug facilities loading state
  useEffect(() => {
    console.log('=== FACILITIES STATE UPDATE ===');
    console.log('facilities:', facilities);
    console.log('facilitiesLength:', facilities?.length);
    console.log('loadingFacilities:', loadingFacilities);
    console.log('facilitiesError:', facilitiesError);
    console.log('buildingIds:', buildingIds);
    console.log('buildingIdsLength:', buildingIds?.length);
    console.log('userAssociations:', userAssociations);
    console.log('userId:', userId);
    console.log('finalIsBuildingAdmin:', finalIsBuildingAdmin);
    console.log('=================================');
  }, [facilities, loadingFacilities, facilitiesError, buildingIds, userAssociations, userId, finalIsBuildingAdmin]);
  
  // facilities list available for selection
  // Find selected facility object
  const facilityObj = facilities.find(f => f.facilityId === selectedFacility);
  
  // Debug facility object with protection against infinite loops
  const [facilityRefreshAttempts, setFacilityRefreshAttempts] = useState({});
  useEffect(() => {
    if (facilityObj && selectedFacility) {
      // If facility has missing slot configuration, trigger a refresh to pick up corrected data
      if (!facilityObj.numberOfSlots && !facilityObj.slots) {
        const attempts = facilityRefreshAttempts[selectedFacility] || 0;
        
        if (attempts < 2) { // Maximum 2 attempts per facility
          console.log(`Facility ${selectedFacility} missing slots config, attempting refresh (attempt ${attempts + 1})`);
          setFacilityRefreshAttempts(prev => ({
            ...prev,
            [selectedFacility]: attempts + 1
          }));
          
          setTimeout(() => {
            refetchFacilities();
          }, 1000);
        } else {
          console.warn(`Facility ${selectedFacility} still missing slots after 2 attempts, stopping refresh`);
        }
      }
    }
  }, [facilityObj, selectedFacility, facilityRefreshAttempts, refetchFacilities]);
  
  // Create stable reference for buildingId to prevent unnecessary effect triggers
  const selectedFacilityBuildingId = useMemo(() => {
    return facilityObj?.buildingId;
  }, [facilityObj]);

  // Find the building name for the selected facility (or first building if none selected)
  const selectedBuilding =
    buildings.find(b => b.buildingId === facilityObj?.buildingId) ||
    buildings[0];

  // Booking mutation with enhanced error handling
  const bookingMutation = useMutation(facilityBookingsAPI.create, {
    onSuccess: () => {
      setSuccess(t('facilities.bookingConfirmed'));
      setError('');
      setSelectedFacility('');
      setSelectedSlot('');
      setBookingDate('');
      setSelectedFlat('');
      queryClient.invalidateQueries(['facilityBookings']);
      refetchBookings();
      // Refresh month slots to update availability
      if (selectedFacility && selectedFacilityBuildingId) {
        // Force refresh month slots after successful booking
        setMonthSlots(prev => ({
          ...prev,
          // This will trigger a fresh fetch on next calendar view
        }));
      }
    },
    onError: (err) => {
      const errorMessage = err.response?.data?.message || err.message || 'Booking failed';
      
      // Handle specific slot exceeded errors
      if (errorMessage.toLowerCase().includes('slot') && 
          (errorMessage.toLowerCase().includes('full') || 
           errorMessage.toLowerCase().includes('exceed') ||
           errorMessage.toLowerCase().includes('booked'))) {
        setError(t('facilities.slotUnavailable'));
      } else {
        setError(errorMessage);
      }
      setSuccess('');
    }
  });

  // Edit booking mutation
  const editBookingMutation = useMutation(facilityBookingsAPI.update, {
    onSuccess: () => {
      setSuccess(t('facilities.bookingUpdated'));
      setError('');
      setEditDialogOpen(false);
      setEditingBooking(null);
      queryClient.invalidateQueries(['facilityBookings']);
      refetchBookings();
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to update booking');
      setSuccess('');
    }
  });

  // Delete booking mutation
  const deleteBookingMutation = useMutation(facilityBookingsAPI.delete, {
    onSuccess: () => {
      setSuccess(t('facilities.bookingDeleted'));
      setError('');
      setDeleteConfirmOpen(false);
      setBookingToDelete(null);
      queryClient.invalidateQueries(['facilityBookings']);
      refetchBookings();
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to delete booking');
      setSuccess('');
    }
  });

  // Fetch slots for the whole month for calendar with slot counts
  useEffect(() => {
    const fetchMonthSlots = async () => {
      if (!selectedFacility || !selectedFacilityBuildingId) {
        setMonthSlots({});
        return;
      }
      
      try {
        // Use the monthly API instead of daily calls
        const res = await facilityBookingsAPI.getByFacilityAndDate(
          selectedFacility, 
          selectedFacilityBuildingId, 
          calendarYear, 
          calendarMonth
        );
        
        // Process the backend response structure { facilityId, facilityName, buildingId, year, month, slotsByDate }
        const slotsObj = {};
        const days = getDaysInMonth(calendarYear, calendarMonth);
        
        // Initialize all days with empty arrays
        for (let d = 1; d <= days; d++) {
          const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          slotsObj[dateStr] = {
            bookedSlots: [],
            bookings: []
          };
        }
        
        // Process the slotsByDate from backend response
        if (res.data && res.data.slotsByDate) {
          Object.entries(res.data.slotsByDate).forEach(([date, dayBookings]) => {
            if (slotsObj[date] && Array.isArray(dayBookings)) {
              dayBookings.forEach(booking => {
                const slot = `${booking.startTime}-${booking.endTime}`;
                slotsObj[date].bookedSlots.push(slot);
                slotsObj[date].bookings.push({
                  ...booking,
                  slot: slot,
                  bookingDate: date
                });
              });
            }
          });
        }
        
        setMonthSlots(slotsObj);
        
      } catch (error) {
        console.error('Error fetching month slots:', error);
        // Initialize empty slots on error
        const slotsObj = {};
        const days = getDaysInMonth(calendarYear, calendarMonth);
        for (let d = 1; d <= days; d++) {
          const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          slotsObj[dateStr] = {
            bookedSlots: [],
            bookings: []
          };
        }
        setMonthSlots(slotsObj);
      }
    };
    
    if (calendarOpen && selectedFacility && selectedFacilityBuildingId) {
      fetchMonthSlots();
    }
  }, [calendarOpen, selectedFacility, calendarMonth, calendarYear, selectedFacilityBuildingId]);

  // Handle booking submit with enhanced slot validation
  const handleBook = async () => {
    if (!selectedFacility || !selectedSlot || !bookingDate || !selectedFlat) {
      setError('Please select all fields.');
      setSuccess('');
      return;
    }

    // Validate facility object exists
    if (!facilityObj) {
      setError('Selected facility information is not available. Please refresh the page and try again.');
      setSuccess('');
      return;
    }

    // Validate charge amount
    const chargeAmount = facilityObj?.chargeAmount;
    const chargeUnit = facilityObj?.chargeUnit;
    
  // Validate charge amount - keep minimal logging for warnings
    
    if (chargeAmount === undefined || chargeAmount === null) {
      console.warn('Charge amount is undefined, defaulting to 0');
    }

    // Enhanced slot availability validation
    try {
      let bookedSlots = [];
      let freshDataFetched = false;
      
      // Try to use cached month slots first
      if (monthSlots[bookingDate]) {
        bookedSlots = monthSlots[bookingDate].bookedSlots;
      } else {
        // Fallback to API call if not in cache
        const slotRes = await facilityBookingsAPI.getByFacilityDate(selectedFacility, facilityObj?.buildingId, bookingDate);
        bookedSlots = Array.isArray(slotRes.data) ? slotRes.data.map(b => b.slot) : [];
        freshDataFetched = true;
      }
      
      const slotBookedCount = bookedSlots.filter(s => s === selectedSlot).length;
      let availableSlots = facilityObj?.numberOfSlots || facilityObj?.slots || 1;
      
      // Handle facilities with missing slot configuration
      if (!facilityObj?.numberOfSlots && !facilityObj?.slots) {
        console.warn('FACILITY CONFIGURATION WARNING: Facility has no slot configuration, defaulting to 10 slots');
        console.warn('Facility object:', facilityObj);
        availableSlots = 10; // Default to 10 slots for facilities without configuration
      }

      // Check calendar data vs API data consistency
      if (monthSlots[bookingDate]) {
        const calendarBookedCount = monthSlots[bookingDate].bookedSlots.filter(s => s === selectedSlot).length;
        if (calendarBookedCount !== slotBookedCount) {
          console.warn('DATA INCONSISTENCY: Calendar shows', calendarBookedCount, 'but API shows', slotBookedCount);
        }
      }

      // Primary validation check
      if (slotBookedCount >= availableSlots) {
        setError(`This time slot is fully booked (${slotBookedCount}/${availableSlots}). Please select another slot.`);
        setSuccess('');
        return;
      }

      // If using cached data and slot appears to be available, 
      // do a fresh check to ensure no race conditions
      if (!freshDataFetched && slotBookedCount > 0) {
        const freshSlotRes = await facilityBookingsAPI.getByFacilityDate(selectedFacility, facilityObj?.buildingId, bookingDate);
        const freshBookedSlots = Array.isArray(freshSlotRes.data) ? freshSlotRes.data.map(b => b.slot) : [];
        const freshSlotBookedCount = freshBookedSlots.filter(s => s === selectedSlot).length;
        
        if (freshSlotBookedCount >= availableSlots) {
          setError(`This time slot was just booked by someone else (${freshSlotBookedCount}/${availableSlots}). Please select another slot.`);
          setSuccess('');
          return;
        }
      }
      // Slot validation passed
      
    } catch (error) {
      console.error('Error checking slot availability:', error);
      setError('Unable to verify slot availability. Please try again.');
      setSuccess('');
      return;
    }

    // Split slot into startTime and endTime
    const [startTime, endTime] = selectedSlot.split('-');
    
    // Prepare booking data with validated amount
    const bookingData = {
      buildingId: facilityObj?.buildingId,
      facilityId: selectedFacility,
      slot: selectedSlot,
      startTime: startTime?.trim(),
      endTime: endTime?.trim(),
      bookingDate: bookingDate,
      userId: userId,
      flatId: selectedFlat,
      amountCharged: chargeAmount || 0,
      chargeUnit: chargeUnit || '',
    };
    
    console.log('Submitting booking with data:', bookingData);
    bookingMutation.mutate(bookingData);
  };

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Manual refresh triggered - clearing related caches
      
      // Force clear all related cache
      queryClient.invalidateQueries(['facilities']);
      queryClient.invalidateQueries(['facilityBookings']);
      queryClient.removeQueries(['facilities', buildingIds]);
      
      const refreshPromises = [
        refetchFacilities(), // Always refetch facilities on manual refresh
        refetchBookings()
      ];
      
      await Promise.all(refreshPromises);
      
  setSuccess('Data refreshed successfully! Cache cleared.');
      setError('');
    } catch (error) {
      setError('Failed to refresh data');
      setSuccess('');
    } finally {
      setRefreshing(false);
    }
  };

  // TEST FUNCTION - Remove after debugging
  const testFacilitiesAPI = async () => {
    console.log('=== MANUAL FACILITIES TEST ===');
    try {
      // Test with a hardcoded building ID first
      const testBuildingId = 'c4428829-db10-4e9c-bcbe-224f14509a8f';
      console.log('Testing facilities API with building ID:', testBuildingId);
      
      const response = await facilitiesAPI.getAll(testBuildingId);
      console.log('Manual facilities API response:', response);
      console.log('Response data:', response?.data);
      
      // Also test with current buildingIds
      if (buildingIds && buildingIds.length > 0) {
        console.log('Testing with current buildingIds:', buildingIds);
        for (const id of buildingIds) {
          const res = await facilitiesAPI.getAll(id);
          console.log(`Facilities for building ${id}:`, res);
        }
      }
    } catch (error) {
      console.error('Manual facilities test error:', error);
      console.error('Error response:', error.response);
    }
    console.log('=== END MANUAL TEST ===');
  };

  

  // Handle edit booking
  const handleEditBooking = async (booking) => {
    console.log('=== OPENING EDIT DIALOG ===');
    console.log('booking to edit:', booking);
    console.log('current facilities:', facilities);
    
    setEditingBooking(booking);
    setEditDialogOpen(true);
    
    // Force refresh facilities to ensure we have the latest slots data
    console.log('Forcing facilities refresh for edit dialog...');
    try {
      await refetchFacilities();
      console.log('Facilities refreshed successfully');
    } catch (error) {
      console.error('Error refreshing facilities:', error);
    }
  };

  // Handle delete booking
  const handleDeleteBooking = (booking) => {
    setBookingToDelete(booking);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (bookingToDelete) {
      deleteBookingMutation.mutate(bookingToDelete.bookingId);
    }
  };

  // Update editing booking
  const handleUpdateBooking = () => {
    if (editingBooking) {
      // Validate required fields
      if (!editingBooking.bookingDate && !editingBooking.date) {
        setError('Please select a booking date');
        return;
      }
      
      if (!editingBooking.slot) {
        setError('Please select a time slot');
        return;
      }
      
      // Prepare the booking data for backend
      const bookingToUpdate = { ...editingBooking };
      
      // If slot was changed, split it into startTime and endTime
      if (bookingToUpdate.slot && bookingToUpdate.slot.includes('-')) {
        const [startTime, endTime] = bookingToUpdate.slot.split('-');
        bookingToUpdate.startTime = startTime;
        bookingToUpdate.endTime = endTime;
      }
      
      // Remove the slot field as backend doesn't expect it for updates
      delete bookingToUpdate.slot;
      
      // Clear any previous errors
      setError('');
      
      editBookingMutation.mutate(bookingToUpdate);
    }
  };

  // Handler for selecting a slot from the calendar
  const handleCalendarSlotSelect = (dateStr, slot, slotStatus) => {
    // Only allow selection of available slots
    if (slotStatus !== 'available') {
      if (slotStatus === 'booked') {
        setError('This time slot is fully booked. Please select another slot.');
      } else if (slotStatus === 'past') {
        setError('Cannot book slots in the past.');
      }
      return;
    }
    
    // Clear any previous errors
    setError('');
    setBookingDate(dateStr);
    setSelectedSlot(slot);
    setCalendarOpen(false);
  };

  // Get slot availability info for a specific date and slot
  const getSlotAvailability = (date, slot) => {
    const daySlots = monthSlots[date];
    let totalSlots = facilityObj?.numberOfSlots || facilityObj?.slots || 1; // Check both field names
    
    // Handle facilities with missing slot configuration
    if (!facilityObj?.numberOfSlots && !facilityObj?.slots) {
      console.warn('CALENDAR FACILITY WARNING: Facility has no slot configuration, defaulting to 10 slots');
      totalSlots = 10; // Default to 10 slots for facilities without configuration
    }
    
    if (!daySlots) {
      return { 
        available: totalSlots, 
        booked: 0, 
        total: totalSlots 
      };
    }
    
    const bookedCount = daySlots.bookedSlots.filter(s => s === slot).length;
    const availableCount = Math.max(0, totalSlots - bookedCount);
    
    return {
      available: availableCount,
      booked: bookedCount,
      total: totalSlots
    };
  };

  // Calendar rendering
  const today = new Date();
  const now = new Date();

  // Restrict calendar to next one month
  const calendarStart = new Date(today.getFullYear(), today.getMonth(), 1); // Start from first day of current month
  const calendarEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  calendarEnd.setMonth(calendarEnd.getMonth() + 1);

  // Helper to check if a date is within the allowed range
  function isDateInAllowedRange(dateStr) {
    const date = new Date(dateStr);
    return date >= calendarStart && date < calendarEnd;
  }

  const daysInMonth = getDaysInMonth(calendarYear, calendarMonth);
  const firstDay = new Date(calendarYear, calendarMonth, 1).getDay(); // 0=Sun

  // Fetch facility bookings for both users and building admins
  const { data: userBookingsRaw = [], isLoading: loadingBookings, refetch: refetchBookings } = useQuery(
    ['facilityBookings', user.userId || userId, finalIsBuildingAdmin ? 'admin' : 'user', buildingIds],
    async () => {
      try {
        if (finalIsBuildingAdmin) {
          // Building admin sees all bookings for their buildings
          if (!Array.isArray(buildingIds) || buildingIds.length === 0) {
            console.warn('No building IDs found for building admin');
            return [];
          }
          
          // Additional check: ensure all buildingIds are valid
          const validBuildingIds = buildingIds.filter(id => id && id !== null && id !== undefined && id !== '');
          if (validBuildingIds.length === 0) {
            console.warn('No valid building IDs found:', buildingIds);
            return [];
          }
          
          const allBookings = await Promise.all(
            validBuildingIds.map(async (id) => {
              try {
                const res = await facilityBookingsAPI.getByBuilding(id);
                return Array.isArray(res.data) ? res.data : [];
              } catch (error) {
                console.error(`Error fetching bookings for building ${id}:`, error);
                return [];
              }
            })
          );
          
          const flatBookings = Array.isArray(allBookings) ? allBookings.flat() : [];
          return flatBookings;
        } else {
          // Regular users see only their bookings
          if (!userId) return [];
          
          const res = await facilityBookingsAPI.getByUser(userId);
          
          if (Array.isArray(res.data)) return res.data;
          if (Array.isArray(res.data?.bookings)) return res.data.bookings;
          return [];
        }
      } catch (error) {
        console.error('Error fetching user bookings:', error);
        return [];
      }
    },
    { 
      enabled: (() => {
        const hasUserId = !!userId;
        const isRegularUser = !finalIsBuildingAdmin;
        const isBuildingAdminWithData = finalIsBuildingAdmin && 
          !loadingAssociations && 
          Array.isArray(buildingIds) && 
          buildingIds.length > 0 &&
          buildingIds.every(id => id !== null && id !== undefined && id !== '');
        
        const shouldEnable = hasUserId && (isRegularUser || isBuildingAdminWithData);
        
        console.log('Facility bookings query enabled check:', {
          shouldEnable,
          hasUserId,
          finalIsBuildingAdmin,
          isRegularUser,
          loadingAssociations,
          buildingIdsLength: buildingIds?.length,
          buildingIds: buildingIds,
          timestamp: new Date().toISOString()
        });
        
        return shouldEnable;
      })(),
      refetchInterval: false, // Disable automatic refetch to stop infinite calls
      staleTime: 60000, // Data is fresh for 1 minute
      cacheTime: 300000 // Keep in cache for 5 minutes
    }
  );
  const userBookings = Array.isArray(userBookingsRaw) ? userBookingsRaw : [];

  // Get current month's start and end dates
  const currentDate = new Date();
  const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const currentMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  // Format dates as YYYY-MM-DD for comparison
  const formatDateString = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const currentMonthStartStr = formatDateString(currentMonthStart);
  const currentMonthEndStr = formatDateString(currentMonthEnd);

  // Filter bookings for current month and apply search
  const filteredBookings = useMemo(() => {
    let filtered = userBookings;
    
    // Filter for current month only
    if (finalIsBuildingAdmin) {
      filtered = filtered.filter(booking => {
        const bookingDate = booking.bookingDate || booking.date;
        return bookingDate >= currentMonthStartStr && bookingDate <= currentMonthEndStr;
      });
    }
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => {
        return (
          (booking.facilityName || booking.facilityId || '').toLowerCase().includes(searchLower) ||
          (booking.bookingDate || booking.date || '').includes(searchLower) ||
          (booking.slot || '').toLowerCase().includes(searchLower) ||
          (booking.userName || booking.userId || '').toLowerCase().includes(searchLower) ||
          (booking.flatName || booking.flatNumber || booking.flatId || '').toLowerCase().includes(searchLower)
        );
      });
    }
    
    return filtered;
  }, [userBookings, finalIsBuildingAdmin, searchTerm, currentMonthStartStr, currentMonthEndStr]);

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Debug effect for bookings query
  useEffect(() => {
    // Bookings query monitoring - no debug logging in production
  }, [userId, finalIsBuildingAdmin, loadingAssociations, buildingIds, userBookingsRaw, userBookings, loadingBookings]);

  // Auto-refetch bookings when building admin data becomes available
  useEffect(() => {
    if (finalIsBuildingAdmin && !loadingAssociations && buildingIds.length > 0 && userBookings.length === 0 && !loadingBookings) {
      setTimeout(() => refetchBookings(), 500); // Small delay to avoid race conditions
    }
  }, [finalIsBuildingAdmin, loadingAssociations, buildingIds, userBookings.length, loadingBookings, refetchBookings]);

  if (userLoading) {
    return <Box sx={{ mt: 4, textAlign: 'center' }}>Loading...</Box>;
  }
  if (!user) {
    return <Box sx={{ mt: 4, textAlign: 'center' }}>Please log in to book a facility.</Box>;
  }

  return (
    <Box
      className="mobile-content-spacing"
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        mt: { xs: 2, sm: 4 },
        px: { xs: 1.5, sm: 2 },
        pb: { xs: 'calc(104px + env(safe-area-inset-bottom, 0px))', md: 0 }
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.75 }}>
              <EventAvailable color="primary" sx={{ fontSize: { xs: 30, sm: 36 } }} />
              <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 700, letterSpacing: 0 }}>
                {t('facilities.booking')}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Reserve shared amenities and track current month bookings.
            </Typography>
          </Box>
          {selectedBuilding && (
            <Chip
              icon={<Apartment />}
              label={selectedBuilding.buildingName}
              variant="outlined"
              sx={{ fontWeight: 700, maxWidth: { xs: '100%', sm: 360 } }}
            />
          )}
        </Box>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          mb: 4,
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, bgcolor: '#f7f9fc', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Book a Facility
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select the flat, facility, date, and available timing slot.
          </Typography>
        </Box>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2.25}>
            {/* Flat selection */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Flat"
                value={selectedFlat}
                onChange={e => setSelectedFlat(e.target.value)}
                fullWidth
                required
                disabled={loadingFlats}
                helperText={
                  loadingFlats 
                    ? 'Loading flats...'
                    : flatsForDropdown.length === 0
                    ? `No flats found for your account. ${isBuildingAdmin ? 'Try checking your building admin permissions.' : 'Contact building admin to associate you with a flat.'}`
                    : ''
                }
              >
                {flatsForDropdown.length === 0 && !loadingFlats ? (
                  <MenuItem disabled>
                    No flats available
                  </MenuItem>
                ) : (
                  flatsForDropdown.map(flat => (
                    <MenuItem key={flat.flatId} value={flat.flatId}>
                      {flat.flatNumber}
                      {flat.buildingName ? ` - ${flat.buildingName}` : ''}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            {/* Building selection removed */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label={t('facilities.facilityName')}
                value={selectedFacility}
                onChange={e => {
                  setSelectedFacility(e.target.value);
                  setSelectedSlot('');
                }}
                fullWidth
                required
                disabled={loadingFacilities}
                helperText={
                  facilities.filter(f => f.status === 'OPEN').length === 0
                    ? t('facilities.noFacilitiesSelection')
                    : ''
                }
              >
                {facilities
                  .filter(f => f.status === 'OPEN')
                  .map(f => {
                    let label = '';
                    if (f.type && f.name) {
                      label = `${f.type} - ${f.name}`;
                    } else if (f.type) {
                      label = f.type;
                    } else if (f.name) {
                      label = f.name;
                    } else {
                      label = t('facilities.noFacilitiesSelection');
                    }
                    return (
                      <MenuItem key={f.facilityId} value={f.facilityId}>
                        {label}
                      </MenuItem>
                    );
                  })}
              </TextField>
            </Grid>
            {facilityObj && (
              <>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={() => setCalendarOpen(true)}
                    disabled={!selectedFacility}
                    startIcon={<CalendarMonth />}
                    sx={{ fontWeight: 700 }}
                  >
                    View Slot Availability Calendar
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Date"
                    type="date"
                    value={bookingDate}
                    onChange={e => {
                      setBookingDate(e.target.value);
                      setSelectedSlot('');
                    }}
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Timing Slot"
                    value={selectedSlot}
                    onChange={e => setSelectedSlot(e.target.value)}
                    fullWidth
                    required
                    disabled={!bookingDate}
                    helperText={!bookingDate ? 'Select a date first' : ''}
                  >
                    {(() => {
                      // Use slots from facility object, or generate from timings as fallback
                      let slotsToUse = facilityObj?.slots;
                      
                      if (!slotsToUse || !Array.isArray(slotsToUse) || slotsToUse.length === 0) {
                        // Fallback: generate slots from timings
                        if (facilityObj?.timings && facilityObj?.numberOfSlots) {
                          slotsToUse = generateSlotsFromTimings(facilityObj.timings, facilityObj.numberOfSlots);
                        }
                      }
                      
                      // Final fallback: use default slots
                      if (!slotsToUse || slotsToUse.length === 0) {
                        const numSlots = facilityObj?.numberOfSlots || 1;
                        const defaultSlots = ['09:00-12:00', '12:00-15:00', '15:00-18:00', '18:00-21:00'];
                        slotsToUse = defaultSlots.slice(0, numSlots);
                      }
                      
                      return slotsToUse.map((slot, idx) => {
                        // Get slot availability info
                        const availability = getSlotAvailability(bookingDate, slot);
                        const slotStatus = getSlotStatus(
                          bookingDate,
                          slot,
                          monthSlots[bookingDate]?.bookedSlots || [],
                          facilityObj?.numberOfSlots || facilityObj?.slots || 10,
                          today,
                          now
                        );
                        return (
                          <MenuItem
                            key={idx}
                            value={slot}
                            disabled={slotStatus === 'past' || slotStatus === 'booked'}
                            style={{
                              color:
                                slotStatus === 'available'
                                  ? 'green'
                                  : slotStatus === 'booked'
                                  ? 'red'
                                  : 'grey'
                            }}
                          >
                            {slot} - {t('facilities.available')}: {availability.available}/{availability.total}
                            {slotStatus === 'booked' ? ` (${t('facilities.full')})` : slotStatus === 'past' ? ` (${t('facilities.past')})` : ''}
                          </MenuItem>
                        );
                      });
                    })()}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography>
                    <b>Charge:</b> ₹{facilityObj.chargeAmount} / {facilityObj.chargeUnit}
                  </Typography>
                  <Typography>
                    <b>Available Slots:</b> {facilityObj.slots || 1} per time slot
                  </Typography>
                  <Chip
                    label={facilityObj.bookingRequired ? 'Booking Required' : 'No Booking Required'}
                    color={facilityObj.bookingRequired ? 'primary' : 'default'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {facilityObj.description}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
        <CardActions sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
          <Button
            variant="contained"
            onClick={handleBook}
            disabled={bookingMutation.isLoading || !selectedFacility || !selectedSlot || !bookingDate}
            size="large"
            sx={{ fontWeight: 700, minWidth: { xs: '100%', sm: 180 } }}
          >
            {bookingMutation.isLoading ? t('facilities.booking') + '...' : t('facilities.bookFacility')}
          </Button>
        </CardActions>
      </Card>

      {/* Calendar Dialog */}
      <Dialog 
        className="complaint-dialog"
        open={calendarOpen} 
        onClose={() => setCalendarOpen(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            minHeight: isMobile ? '100vh' : 'auto',
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          fontSize: { xs: '16px', sm: '20px' },
          position: isMobile ? 'sticky' : 'static',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1,
          borderBottom: isMobile ? '1px solid' : 'none',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ 
              fontSize: { xs: '16px', sm: '18px' },
              fontWeight: 'bold',
              mr: 1,
              wordBreak: 'break-word',
            }}>
              {facilityObj?.facilityName || facilityObj?.name || facilityObj?.type}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
            <IconButton
              onClick={() => {
                // Allow navigation to previous months starting from current month
                const prevMonth = new Date(calendarYear, calendarMonth - 1, 1);
                if (prevMonth >= new Date(calendarStart.getFullYear(), calendarStart.getMonth(), 1)) {
                  if (calendarMonth === 0) {
                    setCalendarMonth(11);
                    setCalendarYear(y => y - 1);
                  } else {
                    setCalendarMonth(m => m - 1);
                  }
                }
              }}
              sx={{ ml: { xs: 0, sm: 2 } }}
              disabled={
                new Date(calendarYear, calendarMonth, 1) <=
                new Date(calendarStart.getFullYear(), calendarStart.getMonth(), 1)
              }
            >
              <ArrowBack />
            </IconButton>
            <IconButton
              onClick={() => {
                // Prevent navigating to months after the allowed range
                const nextMonth = new Date(calendarYear, calendarMonth + 1, 1);
                if (nextMonth < calendarEnd) {
                  if (calendarMonth === 11) {
                    setCalendarMonth(0);
                    setCalendarYear(y => y + 1);
                  } else {
                    setCalendarMonth(m => m + 1);
                  }
                }
              }}
              disabled={
                new Date(calendarYear, calendarMonth, 1) >=
                new Date(calendarEnd.getFullYear(), calendarEnd.getMonth(), 0)
              }
            >
              <ArrowForward />
            </IconButton>
          </Box>
          <IconButton
            onClick={() => setCalendarOpen(false)}
            sx={{ flexShrink: 0 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {new Date(calendarYear, calendarMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(7, minmax(96px, 1fr))' }, gap: { xs: 1, sm: 1 }, mb: 2, overflowX: { xs: 'visible', sm: 'auto' } }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <Box key={day} sx={{ fontWeight: 'bold', textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>{day}</Box>
              ))}
              {Array(firstDay).fill(null).map((_, i) => (
                <Box key={'empty-' + i} sx={{ display: { xs: 'none', sm: 'block' } }} />
              ))}
              {Array(daysInMonth).fill(null).map((_, d) => {
                const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(d + 1).padStart(2, '0')}`;
                const dateObj = new Date(dateStr);
                const isAllowed = isDateInAllowedRange(dateStr);
                const isToday = dateObj.toDateString() === today.toDateString();
                const isPast = dateObj < today;
                // Grey out if not in allowed range or in the past
                const greyedOut = !isAllowed || isPast;
                return (
                  <Box
                    key={dateStr}
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: 1.5,
                      p: { xs: 1.25, sm: 1 },
                      minHeight: { xs: 'auto', sm: 80 },
                      bgcolor: greyedOut ? '#f5f5f5' : '#fff',
                      opacity: greyedOut ? 0.5 : 1
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ textAlign: { xs: 'left', sm: 'center' }, fontWeight: 700, mb: { xs: 1, sm: 0 } }}>
                      {isMobile
                        ? new Date(dateStr).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })
                        : d + 1}
                    </Typography>
                    {(() => {
                      // Use slots from facility object, or generate from timings as fallback
                      let slotsToShow = facilityObj?.slots;
                      
                      if (!slotsToShow || !Array.isArray(slotsToShow) || slotsToShow.length === 0) {
                        // Fallback: generate slots from timings
                        if (facilityObj?.timings && facilityObj?.numberOfSlots) {
                          slotsToShow = generateSlotsFromTimings(facilityObj.timings, facilityObj.numberOfSlots);
                        }
                      }
                      
                      // Final fallback: use default slots
                      if (!slotsToShow || slotsToShow.length === 0) {
                        const numSlots = facilityObj?.numberOfSlots || 1;
                        const defaultSlots = ['09:00-12:00', '12:00-15:00', '15:00-18:00', '18:00-21:00'];
                        slotsToShow = defaultSlots.slice(0, numSlots);
                      }
                      
                      return slotsToShow?.map((slot, idx) => {
                        // For today, only show slots whose end time is after now
                        let slotStatus = 'past';
                        const availability = getSlotAvailability(dateStr, slot);
                        
                        if (!greyedOut) {
                          slotStatus = getSlotStatus(
                            dateStr,
                            slot,
                            monthSlots[dateStr]?.bookedSlots || [],
                            facilityObj?.numberOfSlots || slotsToShow?.length || 1,
                            today,
                            now
                          );
                          // For today, check slot end time
                          if (isToday) {
                            const slotEnd = slot.split('-')[1];
                            const slotEndDate = new Date(dateStr + 'T' + slotEnd + ':00');
                            if (slotEndDate < now) slotStatus = 'past';
                          }
                        }
                      let color = 'grey';
                      if (slotStatus === 'available') color = 'green';
                      if (slotStatus === 'booked') color = 'red';
                      if (slotStatus === 'past') color = 'grey';
                      return (
                        <Box
                          key={slot}
                          sx={{
                            mt: 0.5,
                            px: { xs: 1, sm: 0.5 },
                            py: { xs: 0.75, sm: 0 },
                            borderRadius: 1,
                            fontSize: { xs: 13, sm: 12 },
                            color: '#fff',
                            bgcolor: color,
                            textAlign: 'center',
                            cursor: slotStatus === 'available' && !greyedOut ? 'pointer' : 'not-allowed',
                            opacity: slotStatus === 'available' && !greyedOut ? 1 : 0.6,
                          }}
                          onClick={() => {
                            if (slotStatus === 'available' && !greyedOut) {
                              handleCalendarSlotSelect(dateStr, slot, slotStatus);
                            }
                          }}
                        >
                          <Box>{slot}</Box>
                          <Box sx={{ fontSize: 10 }}>
                            {availability.available}/{availability.total}
                          </Box>
                        </Box>
                      );
                    });
                  })()}
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'center' } }}>
              <Box sx={{ width: 16, height: 16, bgcolor: 'green', borderRadius: 1, display: 'inline-block', mr: 1 }} /> <Typography variant="caption">Available</Typography>
              <Box sx={{ width: 16, height: 16, bgcolor: 'red', borderRadius: 1, display: 'inline-block', mr: 1 }} /> <Typography variant="caption">Booked</Typography>
              <Box sx={{ width: 16, height: 16, bgcolor: 'grey', borderRadius: 1, display: 'inline-block', mr: 1 }} /> <Typography variant="caption">Past/Unavailable</Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Show bookings table for all users */}
      <Box sx={{ 
        mt: 4,
        px: 0,
        '& .MuiCard-root': {
          transition: 'all 0.2s ease-in-out'
        }
      }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 2.5 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ 
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 700,
              color: 'primary.main'
            }}>
              {finalIsBuildingAdmin ? t('facilities.allBookings') : t('facilities.myBookings')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Current month bookings and actions
            </Typography>
          </Box>
          <Chip label={`${filteredBookings.length} booking(s) found`} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        </Box>
        
        {/* Search and controls */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {finalIsBuildingAdmin && (
            <TextField
              placeholder="Search bookings..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page when searching
              }}
              size="small"
              sx={{ minWidth: isMobile ? '100%' : 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </Box>
        
        {isMobile ? (
          // Mobile Card Layout
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {loadingBookings ? (
              <Alert severity="info" sx={{ fontSize: '14px' }}>Loading...</Alert>
            ) : paginatedBookings.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '14px' }}>
                {searchTerm ? 'No bookings found matching your search.' : 'No bookings found.'}
              </Alert>
            ) : (
              paginatedBookings.map((booking) => (
                <Card key={booking.bookingId} variant="outlined" sx={{ 
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease-in-out'
                }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '16px',
                        color: 'primary.main',
                        flex: 1,
                        mr: 1
                      }}>
                        {booking.facilityName || booking.facilityId}
                      </Typography>
                      <Chip 
                        label={booking.status || t('facilities.booked')} 
                        color="success" 
                        size="small" 
                        sx={{ fontSize: '11px', fontWeight: 'bold' }}
                      />
                    </Box>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 'bold' }}>
                          Date
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '14px', mb: 1 }}>
                          {booking.bookingDate || booking.date}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 'bold' }}>
                          Time Slot
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '14px', mb: 1 }}>
                          {booking.slot || `${booking.startTime} - ${booking.endTime}`}
                        </Typography>
                      </Grid>
                      
                      {finalIsBuildingAdmin && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 'bold' }}>
                              User
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '14px', mb: 1 }}>
                              {booking.userName || booking.userId}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary', fontWeight: 'bold' }}>
                              Flat
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '14px', mb: 1 }}>
                              {booking.flatName || booking.flatNumber || booking.flatId}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                      <IconButton
                        variant="outlined"
                        size="small"
                        onClick={() => handleEditBooking(booking)}
                        color="primary"
                        sx={{ 
                          border: '1px solid',
                          borderColor: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white'
                          }
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      {finalIsBuildingAdmin && (
                        <IconButton
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBooking(booking)}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'error.main',
                            '&:hover': {
                              backgroundColor: 'error.light',
                              color: 'white'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        ) : (
          // Desktop Table Layout
          <TableContainer component={Paper} sx={{ 
            overflowX: 'auto',
            width: '100%',
            maxWidth: '100%',
            '& .MuiTable-root': {
              width: '100%',
              minWidth: 'auto',
              tableLayout: 'auto'
            }
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: 'grey.50',
                    width: 'auto',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('facilities.facility')}
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: 'grey.50',
                    width: 'auto',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('facilities.date')}
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: 'grey.50',
                    width: 'auto',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('facilities.slot')}
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: 'grey.50',
                    width: 'auto',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('facilities.status')}
                  </TableCell>
                  {finalIsBuildingAdmin && (
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: 'grey.50',
                      width: 'auto',
                      whiteSpace: 'nowrap'
                    }}>
                      {t('facilities.user')}
                    </TableCell>
                  )}
                  {finalIsBuildingAdmin && (
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: 'grey.50',
                      width: 'auto',
                      whiteSpace: 'nowrap'
                    }}>
                      {t('facilities.flat')}
                    </TableCell>
                  )}
                  <TableCell sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: 'grey.50', 
                    textAlign: 'center',
                    width: 'auto',
                    whiteSpace: 'nowrap'
                  }}>
                    {t('facilities.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingBookings ? (
                  <TableRow>
                    <TableCell colSpan={finalIsBuildingAdmin ? 7 : 5} sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>{t('facilities.loading')}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : paginatedBookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={finalIsBuildingAdmin ? 7 : 5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">
                        {searchTerm ? t('facilities.noBookingsFoundSearch') : t('facilities.noBookingsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedBookings.map((booking, index) => (
                    <TableRow 
                      key={booking.bookingId}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'grey.50' 
                        },
                        backgroundColor: index % 2 === 0 ? 'background.default' : 'grey.25'
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'medium', whiteSpace: 'nowrap' }}>
                        {booking.facilityName || booking.facilityId}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {booking.bookingDate || booking.date}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip 
                          label={booking.slot || `${booking.startTime} - ${booking.endTime}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '12px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip 
                          label={booking.status || t('facilities.booked')}
                          color="success"
                          size="small"
                          sx={{ fontSize: '11px' }}
                        />
                      </TableCell>
                      {finalIsBuildingAdmin && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {booking.userName || booking.userId}
                        </TableCell>
                      )}
                      {finalIsBuildingAdmin && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {booking.flatName || booking.flatNumber || booking.flatId}
                        </TableCell>
                      )}
                      <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <Tooltip title="Edit booking">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditBooking(booking)}
                              color="primary"
                              sx={{ 
                                '&:hover': { 
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {finalIsBuildingAdmin && (
                            <Tooltip title="Delete booking">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteBooking(booking)}
                                color="error"
                                sx={{ 
                                  '&:hover': { 
                                    backgroundColor: 'error.light',
                                    color: 'white'
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {filteredBookings.length > 0 && (
          <Box sx={{ 
            mt: 2, 
            display: 'flex', 
            justifyContent: isMobile ? 'center' : 'flex-end',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            {isMobile ? (
              // Mobile pagination with simplified controls
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBack />}
                  disabled={page === 0}
                  onClick={() => setPage(prev => Math.max(0, prev - 1))}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Previous
                </Button>
                
                <Typography variant="body2" sx={{ 
                  textAlign: 'center',
                  color: 'text.secondary',
                  fontSize: '14px'
                }}>
                  Page {page + 1} of {Math.ceil(filteredBookings.length / rowsPerPage)}
                  <br />
                  ({filteredBookings.length} total)
                </Typography>
                
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForward />}
                  disabled={page >= Math.ceil(filteredBookings.length / rowsPerPage) - 1}
                  onClick={() => setPage(prev => Math.min(Math.ceil(filteredBookings.length / rowsPerPage) - 1, prev + 1))}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  Next
                </Button>
              </Box>
            ) : (
              // Desktop pagination
              <TablePagination
                rowsPerPageOptions={[5, 10, 20, 50]}
                component="div"
                count={filteredBookings.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Bookings per page:"
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '14px'
                  }
                }}
              />
            )}
          </Box>
        )}
        </Paper>
      </Box>

      {/* Edit Booking Dialog */}
      <Dialog 
        className="complaint-dialog"
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            minHeight: isMobile ? '100vh' : 'auto',
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '18px', sm: '20px' },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: isMobile ? 'sticky' : 'static',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1
        }}>
          Edit Booking
          {isMobile && (
            <IconButton
              onClick={() => setEditDialogOpen(false)}
              edge="end"
              sx={{ color: 'text.secondary' }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          padding: { xs: 1, sm: 2 },
          paddingTop: { xs: 1, sm: 2 }
        }}>
          {loadingFacilities && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress size={20} />
              <Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                Loading facility slots...
              </Typography>
            </Box>
          )}
          {editingBooking && (
            <Box sx={{ pt: { xs: 1, sm: 2 } }}>
              <TextField
                label="Date"
                type="date"
                value={editingBooking.bookingDate || editingBooking.date || ''}
                onChange={(e) => setEditingBooking({
                  ...editingBooking,
                  bookingDate: e.target.value
                })}
                fullWidth
                margin="normal"
                size={isMobile ? "small" : "medium"}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split('T')[0] // Prevent selecting past dates
                }}
                helperText="Select a future date for your booking"
                sx={{
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '11px', sm: '12px' }
                  }
                }}
              />
              <TextField
                label="Slot"
                select
                value={editingBooking.slot || ''}
                onChange={(e) => setEditingBooking({
                  ...editingBooking,
                  slot: e.target.value
                })}
                fullWidth
                margin="normal"
                size={isMobile ? "small" : "medium"}
                helperText="Select a time slot for your booking"
                sx={{
                  '& .MuiFormHelperText-root': {
                    fontSize: { xs: '11px', sm: '12px' }
                  }
                }}
              >
                {/* Find the facility for this booking to get available slots */}
                {(() => {
                  console.log('=== EDIT DIALOG FACILITY LOOKUP ===');
                  console.log('editingBooking:', editingBooking);
                  console.log('editingBooking.facilityId:', editingBooking?.facilityId);
                  console.log('facilities array:', facilities);
                  console.log('facilities length:', facilities?.length);
                  
                  const facility = facilities.find(f => f.facilityId === editingBooking.facilityId);
                  console.log('found facility:', facility);
                  console.log('facility.slots:', facility?.slots);
                  console.log('facility.timings:', facility?.timings);
                  console.log('facility.numberOfSlots:', facility?.numberOfSlots);
                  
                  if (!facility) {
                    console.warn('No facility found for facilityId:', editingBooking?.facilityId);
                    // Provide emergency fallback slots
                    const emergencySlots = ['09:00-12:00', '12:00-15:00', '15:00-18:00'];
                    return emergencySlots.map((slot) => (
                      <MenuItem key={slot} value={slot}>
                        {slot} (default)
                      </MenuItem>
                    ));
                  }
                  
                  let slotsToRender = facility.slots;
                  
                  if (!slotsToRender || !Array.isArray(slotsToRender) || slotsToRender.length === 0) {
                    console.warn('No slots available for facility:', facility);
                    
                    // Try to generate slots from timings and numberOfSlots as fallback
                    if (facility.timings && facility.numberOfSlots) {
                      console.log('Attempting to generate slots from timings as fallback...');
                      const generatedSlots = generateSlotsFromTimings(facility.timings, facility.numberOfSlots);
                      console.log('Generated slots:', generatedSlots);
                      
                      if (generatedSlots && generatedSlots.length > 0) {
                        console.log('Using generated slots');
                        slotsToRender = generatedSlots;
                      }
                    }
                    
                    // Final fallback - just provide standard slots
                    if (!slotsToRender || slotsToRender.length === 0) {
                      console.log('Using final fallback slots');
                      const numSlots = facility.numberOfSlots || 3;
                      const fallbackSlots = ['09:00-12:00', '12:00-15:00', '15:00-18:00', '18:00-21:00'];
                      slotsToRender = fallbackSlots.slice(0, numSlots);
                    }
                  }
                  
                  console.log('Final slots to render:', slotsToRender);
                  return slotsToRender.map((slot) => (
                    <MenuItem key={slot} value={slot}>
                      {slot}
                    </MenuItem>
                  ));
                })()}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          padding: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          position: isMobile ? 'sticky' : 'static',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: isMobile ? '1px solid' : 'none',
          borderColor: 'divider'
        }}>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleUpdateBooking}
            disabled={editBookingMutation.isLoading}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              minHeight: { xs: '44px', sm: '36px' },
              fontWeight: 'bold',
              color: 'white',
              '&:disabled': {
                color: 'rgba(255, 255, 255, 0.7)'
              }
            }}
          >
            {editBookingMutation.isLoading ? 'Updating...' : 'Update'}
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setEditDialogOpen(false)}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              minHeight: { xs: '44px', sm: '36px' }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        className="complaint-dialog"
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            minHeight: isMobile ? '100vh' : 'auto',
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '18px', sm: '20px' },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: isMobile ? 'sticky' : 'static',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 1
        }}>
          Confirm Delete
          {isMobile && (
            <IconButton
              onClick={() => setDeleteConfirmOpen(false)}
              edge="end"
              sx={{ color: 'text.secondary' }}
            >
              <Close />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          padding: { xs: 2, sm: 3 }
        }}>
          <Typography sx={{ fontSize: { xs: '14px', sm: '16px' }, mb: 2 }}>
            Are you sure you want to delete this booking?
          </Typography>
          {bookingToDelete && (
            <Box sx={{ 
              mt: 2, 
              p: { xs: 1.5, sm: 2 }, 
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1 }}>
                <strong>Facility:</strong> {bookingToDelete.facilityName || bookingToDelete.facilityId}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' }, mb: 1 }}>
                <strong>Date:</strong> {bookingToDelete.bookingDate || bookingToDelete.date}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                <strong>Slot:</strong> {bookingToDelete.slot}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          padding: { xs: 2, sm: 3 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          position: isMobile ? 'sticky' : 'static',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: isMobile ? '1px solid' : 'none',
          borderColor: 'divider'
        }}>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleteBookingMutation.isLoading}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              minHeight: { xs: '44px', sm: '36px' }
            }}
          >
            {deleteBookingMutation.isLoading ? 'Deleting...' : 'Delete Booking'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setDeleteConfirmOpen(false)}
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            sx={{ 
              fontSize: { xs: '14px', sm: '16px' },
              minHeight: { xs: '44px', sm: '36px' }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacilityBooking;
